<template>
  <div class="stats-chart-card">
    <div class="chart-header" @click="expanded = !expanded">
      <span class="chart-title">Solve Times Over Time</span>
      <i :class="['fas', expanded ? 'fa-chevron-up' : 'fa-chevron-down', 'toggle-icon']"></i>
    </div>
    <div v-show="expanded" class="chart-body">
      <div class="day-pills" v-if="availableDays.length > 1">
        <button
          :class="['day-pill', { active: selectedDays === null }]"
          @click="selectedDays = null"
        >All</button>
        <button
          v-for="day in availableDays"
          :key="day"
          :class="['day-pill', { active: selectedDays !== null && selectedDays.includes(day), faded: selectedDays !== null && !selectedDays.includes(day) }]"
          :style="pillStyle(day)"
          @click="toggleDay(day)"
        >{{ day.slice(0, 3) }}</button>
      </div>
      <div v-if="hasEnoughData" class="chart-canvas-wrapper">
        <canvas ref="chartCanvas"></canvas>
      </div>
      <div v-else class="chart-empty">
        <i class="fas fa-sync-alt"></i>
        <p>Sync more solves to view historical data</p>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_COLORS = {
  Sunday:    '#e05252',
  Monday:    '#4a90d9',
  Tuesday:   '#3aac7a',
  Wednesday: '#e09c3a',
  Thursday:  '#9b6dcc',
  Friday:    '#d96a3a',
  Saturday:  '#3aaccc',
};
const SIZE_COLORS = {
  '5×5': '#4a90d9',
  '6×5': '#3aac7a',
  '7×7': '#9b6dcc',
};

function seriesColor(day) {
  return DAY_COLORS[day] || SIZE_COLORS[day] || '#64b5f6';
}

function formatTime(secs) {
  if (secs == null || secs < 0) return 'N/A';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function dateToTs(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default {
  name: 'StatsChart',
  props: {
    chartData: { type: Object, default: () => ({}) },
    puzzleType: { type: String, default: 'daily' },
  },
  data() {
    return {
      expanded: true,
      selectedDays: null,  // null = all, array = specific days
      chart: null,
    };
  },
  computed: {
    availableDays() {
      // For daily: preserve day-of-week order. For other types: use keys as-is.
      const keys = Object.keys(this.chartData).filter(k => this.chartData[k]?.length > 0);
      return DAY_ORDER.filter(d => keys.includes(d)).concat(keys.filter(k => !DAY_ORDER.includes(k)));
    },
    hasEnoughData() {
      const total = this.availableDays.reduce((sum, day) => sum + this.chartData[day].length, 0);
      return total >= 10;
    },
    datasets() {
      const days = this.selectedDays === null
        ? this.availableDays
        : this.availableDays.filter(d => this.selectedDays.includes(d));
      const result = [];
      for (const day of days) {
        const color = seriesColor(day);
        const data = this.chartData[day].map(p => ({ x: dateToTs(p.x), y: p.time, dateStr: p.x }));

        if (this.selectedDays !== null) {
          const dense = data.length > 60;
          const mobile = window.innerWidth < 600;
          result.push({
            type: 'scatter',
            label: day,
            data,
            backgroundColor: hexToRgba(color, 0.55),
            borderColor: 'transparent',
            pointRadius: dense || mobile ? (mobile ? 1.5 : 2) : 4,
            pointHoverRadius: dense || mobile ? (mobile ? 3 : 4) : 6,
          });
        }

        const avgData = this.chartData[day]
          .filter(p => p.avgTime != null)
          .map(p => ({ x: dateToTs(p.x), y: p.avgTime }));
        if (avgData.length >= 2) {
          result.push({
            type: 'line',
            label: `${day}_trend`,
            data: avgData,
            borderColor: color,
            borderWidth: 1.5,
            borderDash: [],
            pointRadius: 0,
            fill: false,
            isTrend: true,
          });
        }
      }
      return result;
    },
  },
  watch: {
    selectedDays() { this.updateChart(); },
    expanded(val) {
      if (val) {
        this.$nextTick(() => {
          if (!this.chart) this.initChart();
          else this.chart.resize();
        });
      }
    },
  },
  mounted() {
    if (this.expanded && this.hasEnoughData) this.initChart();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    toggleDay(day) {
      if (this.selectedDays === null) {
        this.selectedDays = [day];
      } else if (this.selectedDays.includes(day)) {
        const remaining = this.selectedDays.filter(d => d !== day);
        this.selectedDays = remaining.length ? remaining : null;
      } else {
        this.selectedDays = [...this.selectedDays, day];
      }
    },
    pillStyle(day) {
      const color = seriesColor(day);
      if (this.selectedDays !== null && this.selectedDays.includes(day)) {
        return { background: color, borderColor: color, color: '#fff' };
      }
      return { borderColor: color, color: color };
    },
    initChart() {
      const ctx = this.$refs.chartCanvas.getContext('2d');
      this.chart = new Chart(ctx, {
        type: 'scatter',
        data: { datasets: this.datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', intersect: true },
          onClick: (event, elements) => {
            const el = this.selectedDays !== null &&
              elements.find(e => !this.chart.data.datasets[e.datasetIndex].isTrend);
            if (el) {
              const point = this.chart.data.datasets[el.datasetIndex].data[el.index];
              const url = this.puzzleType === 'daily' ? `/puzzles/${point.dateStr}` : `/puzzles/${this.puzzleType}/${point.dateStr}`;
              window.location.href = url;
            }
          },
          onHover: (event, elements) => {
            const hovering = elements.some(e => !this.chart.data.datasets[e.datasetIndex].isTrend);
            event.native.target.style.cursor = hovering ? 'pointer' : 'default';
          },
          scales: {
            x: {
              type: 'linear',
              ticks: {
                color: '#aaa',
                font: { size: 11 },
                maxTicksLimit: window.innerWidth < 600 ? 4 : 8,
                callback: (val) => new Date(val).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
              },
              grid: { color: 'rgba(0,0,0,0.06)' },
              border: { color: 'rgba(0,0,0,0.15)' },
            },
            y: {
              ticks: {
                color: '#aaa',
                font: { size: 11 },
                callback: (val) => formatTime(val),
              },
              grid: { color: 'rgba(0,0,0,0.06)' },
              border: { color: 'rgba(0,0,0,0.15)' },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              filter: (item) => !item.dataset.isTrend,
              callbacks: {
                title: (items) => {
                  if (!items.length) return '';
                  return new Date(items[0].parsed.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                },
                label: (ctx) => ` ${ctx.dataset.label}: ${formatTime(ctx.parsed.y)}`,
              },
            },
          },
        },
      });
    },
    updateChart() {
      if (!this.chart) return;
      this.chart.data.datasets = this.datasets;
      this.chart.update();
    },
  },
};
</script>
